import React, { forwardRef } from "react";
import {
    AriaButtonOptions,
    VisuallyHidden,
    useButton,
    useFocusRing,
    useObjectRef,
} from "react-aria";
import {
    ButtonContext,
    useContextProps,
    useSlottedContext,
} from "react-aria-components";
import { t } from "ttag";

import { SimpleLinkBlock } from "@reactivated";

import { Clickable as BaseClickable } from "@thelabnyc/thelabui/src/components/Clickable";
import { ClickableProps as BaseClickableProps } from "@thelabnyc/thelabui/src/components/Clickable/types";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { ReactivatedContext as Context } from "../../context";
import { SVGName, Svg, allSvgNames } from "../Svg";

import styles from "./index.module.scss";

const simpleLinkToProps = (simpleLink: SimpleLinkBlock | undefined) => {
    if (!simpleLink) return {};
    return {
        target:
            simpleLink.link_type === "external"
                ? ("_blank" as const)
                : ("_self" as const),
        rel:
            simpleLink.link_type === "external"
                ? "noopener noreferrer"
                : undefined,
        href: simpleLink.url ?? undefined,
    };
};

export type ClickableProps = BaseClickableProps &
    AriaButtonOptions<"button" | "a"> & {
        simpleLink?: SimpleLinkBlock;
    };
// new URL(input, base) handles both absolute and relative inputs (base is
// ignored when input is absolute). Wrapped in try/catch since it throws on
// invalid input; URL.parse would be cleaner but isn't supported in Safari < 17.
const tryParseURL = (input: string, base?: string): URL | null => {
    try {
        return new URL(input, base);
    } catch {
        return null;
    }
};
const appendRelTokens = (
    existing: string | undefined,
    ...required: string[]
): string => {
    const tokens = new Set(existing?.split(/\s+/).filter(Boolean) ?? []);
    for (const token of required) tokens.add(token);
    return [...tokens].join(" ");
};
export const Clickable = forwardRef<
    HTMLButtonElement | HTMLAnchorElement,
    ClickableProps
>((baseProps, baseRef) => {
    // Merge slot props from ButtonContext
    // @ts-expect-error ButtonContext is typed for HTMLButtonElement but props work on anchors too
    // eslint-disable-next-line prefer-const
    let [props, ref] = useContextProps(baseProps, baseRef, ButtonContext);

    ref = useObjectRef(ref);
    const reactivatedContext = React.useContext(Context);
    const buttonContext = useSlottedContext(ButtonContext, props.slot);
    const { buttonProps } = useButton({ ...props, ...buttonContext }, ref);
    const { focusProps, isFocused, isFocusVisible } = useFocusRing(props);
    const propsWithContext = props as ClickableProps & {
        isPressed?: boolean;
    };
    const {
        elementType,
        excludeFromTabOrder,
        isDisabled,
        isPressed,
        onClick,
        onPress,
        onPressChange,
        onPressEnd,
        onPressStart,
        onPressUp,
        preventFocusOnPress,
        simpleLink,
        ...propsWithoutOddballs
    } = propsWithContext;
    const simpleLinkProps = simpleLinkToProps(simpleLink);
    const href = simpleLinkProps.href ?? props.href;
    const isExternal = (() => {
        if (!href) return false;
        const url = tryParseURL(href, reactivatedContext.request.url);
        const here = tryParseURL(reactivatedContext.request.url);
        return !!(
            url &&
            here &&
            url.protocol.startsWith("http") &&
            url.host !== here.host
        );
    })();
    const externalLinkProps = isExternal
        ? {
              target: "_blank" as const,
              rel: appendRelTokens(props.rel, "noopener", "noreferrer"),
          }
        : null;

    const childProps = {
        ref,
        ...buttonProps,
        ...focusProps,
        ...propsWithoutOddballs,
        ...simpleLinkProps,
        ...externalLinkProps,
        "className": concatClassNames([styles.base, props.className]),
        "data-focused": isFocused || undefined,
        "data-focus-visible": isFocusVisible || undefined,
    };

    return (
        <BaseClickable {...childProps}>
            {props.children ?? simpleLink?.display_name}
            {isExternal && (
                <VisuallyHidden>{t`(opens in new tab)`}</VisuallyHidden>
            )}
        </BaseClickable>
    );
});

type InnerProps = ClickableProps;

const Inner = ({ ...props }: InnerProps) => {
    let icon = <></>;
    if (
        typeof props.icon === "string" &&
        (allSvgNames as ReadonlyArray<string>).includes(props.icon)
    ) {
        icon = (
            <Svg
                name={props.icon as SVGName}
                visuallyHiddenText={null}
                aria-hidden={true}
            />
        );
    } else if (props.icon && typeof props.icon !== "string") {
        icon = props.icon;
    }
    return (
        <div className={styles.clickableContent}>
            {props.children ?? props.simpleLink?.display_name}
            {icon}
        </div>
    );
};

export const ButtonPrimary = forwardRef<
    HTMLButtonElement | HTMLAnchorElement,
    ClickableProps
>(function ButtonPrimary(props, ref) {
    return (
        <Clickable
            {...props}
            ref={ref}
            className={concatClassNames([
                styles.primaryButton,
                props.disabled ? styles.disabled : undefined,
                props.className ?? undefined,
            ])}
        >
            <Inner {...props} />
        </Clickable>
    );
});

export const ButtonSecondary = forwardRef<
    HTMLButtonElement | HTMLAnchorElement,
    ClickableProps
>(function ButtonSecondary(props, ref) {
    return (
        <Clickable
            {...props}
            ref={ref}
            className={concatClassNames([
                styles.buttonSecondary,
                props.disabled ? styles.disabled : undefined,
                props.className ?? undefined,
            ])}
        >
            <Inner {...props} />
        </Clickable>
    );
});
